import { useEffect } from "react";
import { create } from "zustand";
import { db } from "../firebase/config";

const cardCatalogInflight = new Map();

const setCatalogSlice = (set, key, cards) => {
  set((state) => ({
    [key]: cards,
    catalogLoaded: {
      ...state.catalogLoaded,
      [key === "infinityCards" ? "infinity" : "character"]: true,
    },
    catalogLoadedAt: {
      ...state.catalogLoadedAt,
      [key === "infinityCards" ? "infinity" : "character"]: Date.now(),
    },
  }));
};

const setCatalogLoading = (set, key, value) => {
  set((state) => {
    const nextCatalogLoading = {
      ...state.catalogLoading,
      [key]: value,
    };
    return {
      catalogLoading: nextCatalogLoading,
      isLoading: Object.values(nextCatalogLoading).some(Boolean),
    };
  });
};

const readCollectionCachedFirst = async (collectionName, mapDocs, onCached) => {
  const ref = db.collection(collectionName);

  try {
    const cachedSnap = await ref.get({ source: "cache" });
    if (!cachedSnap.empty) {
      onCached?.(mapDocs(cachedSnap.docs));
    }
  } catch (_error) {
    // Cache miss is fine; we'll fall through to the network-backed read.
  }

  const snap = await ref.get();
  return mapDocs(snap.docs);
};

export const useCardStore = create((set, get) => ({
  infinityCards: [],
  characterCards: [],
  isLoading: false,
  error: null,
  catalogLoading: {
    infinity: false,
    character: false,
  },
  catalogLoaded: {
    infinity: false,
    character: false,
  },
  catalogLoadedAt: {
    infinity: 0,
    character: 0,
  },

  fetchCards: async ({
    includeInfinity = true,
    includeCharacter = true,
    force = false,
  } = {}) => {
    const state = get();
    const needsInfinity = includeInfinity && (force || !state.catalogLoaded.infinity);
    const needsCharacter =
      includeCharacter && (force || !state.catalogLoaded.character);

    if (!needsInfinity && !needsCharacter) {
      return {
        infinityCards: state.infinityCards,
        characterCards: state.characterCards,
      };
    }

    const promiseKeys = [
      needsInfinity ? "infinity" : null,
      needsCharacter ? "character" : null,
    ]
      .filter(Boolean)
      .sort()
      .join("+");

    if (!force && cardCatalogInflight.has(promiseKeys)) {
      return cardCatalogInflight.get(promiseKeys);
    }

    const promise = (async () => {
      try {
        if (needsInfinity) {
          setCatalogLoading(set, "infinity", true);
        }
        if (needsCharacter) {
          setCatalogLoading(set, "character", true);
        }
        set({ error: null });

        const [nextInfinityCards, nextCharacterCards] = await Promise.all([
          needsInfinity
            ? readCollectionCachedFirst(
                "Infinity cards",
                (docs) => docs.map((doc) => ({ id: doc.id, ...doc.data() })),
                (cachedCards) => setCatalogSlice(set, "infinityCards", cachedCards),
              )
            : Promise.resolve(state.infinityCards),
          needsCharacter
            ? readCollectionCachedFirst(
                "Character cards",
                (docs) =>
                  docs
                    .map((doc) => ({ id: doc.id, ...doc.data() }))
                    .filter((card) => !!card.ipProviderStripeId),
                (cachedCards) => setCatalogSlice(set, "characterCards", cachedCards),
              )
            : Promise.resolve(state.characterCards),
        ]);

        if (needsInfinity) {
          setCatalogSlice(set, "infinityCards", nextInfinityCards);
        }
        if (needsCharacter) {
          setCatalogSlice(set, "characterCards", nextCharacterCards);
        }

        return {
          infinityCards: needsInfinity ? nextInfinityCards : get().infinityCards,
          characterCards: needsCharacter
            ? nextCharacterCards
            : get().characterCards,
        };
      } catch (err) {
        console.error("Error fetching cards:", err);
        set({ error: err.message });
        throw err;
      } finally {
        if (needsInfinity) {
          setCatalogLoading(set, "infinity", false);
        }
        if (needsCharacter) {
          setCatalogLoading(set, "character", false);
        }
      }
    })();

    cardCatalogInflight.set(promiseKeys, promise);
    promise.finally(() => {
      cardCatalogInflight.delete(promiseKeys);
    });

    return promise;
  },

  invalidateCards: (...keys) => {
    const invalidateAll = keys.length === 0;
    set((state) => ({
      catalogLoaded: {
        infinity:
          invalidateAll || keys.includes("infinity")
            ? false
            : state.catalogLoaded.infinity,
        character:
          invalidateAll || keys.includes("character")
            ? false
            : state.catalogLoaded.character,
      },
      catalogLoadedAt: {
        infinity:
          invalidateAll || keys.includes("infinity")
            ? 0
            : state.catalogLoadedAt.infinity,
        character:
          invalidateAll || keys.includes("character")
            ? 0
            : state.catalogLoadedAt.character,
      },
    }));
  },
}));

export const useEnsureCardCatalog = ({
  enabled = true,
  infinity = true,
  character = true,
  force = false,
} = {}) => {
  const fetchCards = useCardStore((state) => state.fetchCards);
  const catalogLoaded = useCardStore((state) => state.catalogLoaded);
  const catalogLoading = useCardStore((state) => state.catalogLoading);

  useEffect(() => {
    if (!enabled) return;

    const needsInfinity = infinity && !catalogLoaded.infinity && !catalogLoading.infinity;
    const needsCharacter =
      character && !catalogLoaded.character && !catalogLoading.character;

    if (!needsInfinity && !needsCharacter && !force) return;

    fetchCards({
      includeInfinity: infinity,
      includeCharacter: character,
      force,
    }).catch((error) => {
      console.error("Failed to ensure card catalog:", error);
    });
  }, [
    enabled,
    infinity,
    character,
    force,
    fetchCards,
    catalogLoaded.infinity,
    catalogLoaded.character,
    catalogLoading.infinity,
    catalogLoading.character,
  ]);
};
